import React, { useState, useRef } from "react";
import { View } from "react-native";
import { useRouter } from "expo-router";
import {
  ClientCreateForm,
  ClientCreateFormRef,
  ClientPayload,
  CLIENT_STATUS_OPTIONS,
  getClientStatusColor,
  ClientStatusType,
} from "@/components/screens/client/ClientCreateForm";
import { CreateHeader } from "@/components/screens/shared";
import { useTheme } from "@/hooks/useTheme";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";
import { clientService } from "@/services/clientService";
import Head from "expo-router/head";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { getSaveBehavior } from "@/lib/saveSettings";

export default function NewClientPage() {
  const toast = useToast();
  const router = useRouter();
  const { tenantId } = useAuth();
  const { colors } = useTheme();
  const { triggerRefresh } = useRowSelectionContext();
  const formRef = useRef<ClientCreateFormRef>(null);
  const [dirty, setDirty] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<ClientStatusType>("ACTIVE");

  const handleReset = () => {
    setName("");
    setDescription("");
    setStatus("ACTIVE");
    formRef.current?.handleReset();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Client</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="client"
          name={name}
          onNameChange={(value) => {
            setName(value);
            formRef.current?.updateName(value);
          }}
          namePlaceholder="Client name"
          description={description}
          onDescriptionChange={(value) => {
            setDescription(value);
            formRef.current?.updateDescription(value);
          }}
          descriptionPlaceholder="Brief description"
          dirty={dirty}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
          status={status}
          onStatusChange={(value) => {
            setStatus(value as ClientStatusType);
            formRef.current?.updateStatus(value);
          }}
          statusOptions={CLIENT_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getClientStatusColor(opt.text, colors),
          }))}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <ClientCreateForm
            ref={formRef}
            initialClient={{ name, description, status }}
            onChange={() => {}}
            onSave={async (payload: ClientPayload) => {
              // Extract client_contacts before creating client
              const { client_contacts, ...clientFields } = payload.client;

              // Create client (don't include client_contacts in DB payload)
              const clientData = {
                name: clientFields.name,
                description: clientFields.description,
                status: clientFields.status,
                meta_data: clientFields.meta_data,
                tenant_id: tenantId!,
              };
              const newClient = await clientService.addClient(clientData);

              // Sync client_contacts to link table if any selected
              if (newClient?.id && client_contacts && client_contacts.length > 0) {
                await clientService.syncClientContacts(newClient.id, client_contacts);
              }

              toast.showToast({ title: "Client created successfully", toastType: "success" });
              triggerRefresh("client");

              // Handle save behavior setting
              const behavior = await getSaveBehavior("client");
              switch (behavior) {
                case "view_created":
                  router.setParams({ new: undefined, id: newClient?.id });
                  break;
                case "create_another":
                  handleReset();
                  break;
                case "close":
                default:
                  router.setParams({ new: undefined, panel: undefined });
                  break;
              }
            }}
            onDirtyChange={setDirty}
          />
        </View>
      </View>
    </>
  );
}
